import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import styled from "styled-components"
import PropTypes from 'prop-types'

// UTILS
import { colorIsDark } from 'Utils/colors'

// DEFAULT CUSTOM ELEMENTS
import CustomMultiOption from './components/CustomMultiOption'
import CustomMultiValue from './components/CustomMultiValue'

const GenericFilterContainer = styled.div`
  height: 100%;
  input {
    min-height: 20px;
  }
`
const defaultIdFor_All_Element = "allOptionsOfFilter123456"

const GenericFilter = ({
  options, 
  placeholder,
  ariaLabel,
  onChange,
  partialTrad,
  defaultValue,
  value,
  useSelectAllOption,
  menuIsOpen,
  enableCustomMenuToggle,
  isSearchable,
  isClearable,
  borderRadius,
  noOptionsMessage,
  customMultiOption,
  customMultiValue,
  isMulti,
  inputTextColor,
  inputHeight,
  inputBackgroundColor,
}) => {

  const getState = (useSelectAllOption, isMulti, options, value) => {
    var optionAll = {
      value: defaultIdFor_All_Element,
      label: partialTrad?.SELECT_ALL
    }
    var options = [
      ...((useSelectAllOption && isMulti) ? [optionAll] : []),
      ...options
    ]
    return {
      options: options,
      values: value
    }
  }

  const [dataToUse, setDataToUse] = useState(getState(useSelectAllOption, isMulti, options, value))

  useEffect(() => {
    if(useSelectAllOption && isMulti) {
      var valueLength = 0
      if(Array.isArray(value)) valueLength = value.length
      else if(value !== undefined) valueLength = 0
  
      var optionsLength = options.length
      var allShouldBeSelected = valueLength === optionsLength
  
      var newValues = []
      if(allShouldBeSelected) {
        newValues = [
          {
            value: defaultIdFor_All_Element,
            label: partialTrad.SELECT_ALL
          }, 
          ...options
        ]
      } else {
        newValues = value !== undefined ? value : []
      }
  
      setDataToUse({
        options: [
          {
            value: defaultIdFor_All_Element,
            label: partialTrad.SELECT_ALL
          }, 
          ...options
        ],
        values: newValues
      })
    } else {
      var newState = getState(useSelectAllOption, isMulti, options, value)
      setDataToUse({...newState})
    }
  }, [options, value])

  const inputBackgroundColorIsDark = colorIsDark(inputBackgroundColor)
  // if there is no color choosed for the input text then choose white or black according to the input background color 
  const textColor = inputTextColor !== "" ? inputTextColor : inputBackgroundColorIsDark ? "#ffffff" : "#000000"

  const customStyles = {
    container: styles => ({
        ...styles,
      height: "100%",
    }),
    control: styles => ({
        ...styles,
      backgroundColor: inputBackgroundColor,
      // height: inputHeight,
      height: "100%",
      color: textColor,
      border: 0,
      boxShadow: 0,
      borderRadius: borderRadius || "0px",
    }),
    dropdownIndicator: styles => ({
      ...styles,
      color: textColor,
      cursor: "pointer",
      transition: "0.2s",
      '&:hover': {
        color: textColor,
        opacity: "0.5",
      }
    }),
    placeholder: styles => ({
      ...styles,
      color: textColor + "80",
      fontStyle: "italic",
    }),
    clearIndicator: styles => ({
      ...styles,
      color: textColor,
      cursor: "pointer",
      transition: "0.2s",
      '&:hover': {
        color: textColor,
        opacity: "0.5",
      }
    }),
    singleValue: styles => ({
      ...styles,
      color: textColor,
    }),
    menu: styles => ({
      ...styles,
      marginTop: 0,
    }),
    input: styles => ({
      ...styles,
      color: textColor,
      minHeight: "20px"
    }),
    valueContainer: styles => ({
      ...styles,
      height: "100%",
      padding: "0 8px",
      alignItems: "center",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflowX: "hidden",
      overflowY: "auto !important",
      '::-webkit-scrollbar-button': {
        height: "3px",
      },

    }),
    indicatorsContainer: styles => ({
      ...styles,
      height: "100%",
    }),
  }

  // if the component should display a multi select, it have to use the custom options and the custom values
  const multiSelectObj = {
    MultiValue: customMultiValue,
    Option: customMultiOption
  }
  const components = isMulti  ? multiSelectObj : {}
  
  // if custom menu toggle is enable then give the "menuIsOpen" props to the Select component
  const menuIsOpenProps = enableCustomMenuToggle ? {menuIsOpen} : {}
  
  //manage if options list will be not hidden
  const onMenuOpen = () => {
    var fixedLayout = document.getElementById('resizable')
    if(fixedLayout) {
      fixedLayout.style.removeProperty("overflow")
      fixedLayout.style.removeProperty("overflow-y")
    }
  }

  const onMenuClose = () => {
    var fixedLayout = document.getElementById('resizable')
    if(fixedLayout) fixedLayout.style.setProperty('overflow-y', "auto")
  }

  const isOption_All = valueSelected => valueSelected.value === defaultIdFor_All_Element

  const isNotOption_All = valueSelected => valueSelected.value !== defaultIdFor_All_Element

  const onChangeMiddleWare = (optionsSelected) => {
    if(!optionsSelected) onChange({value: "", label: ""})

    var dataSelectorToSend = {}
    if(Array.isArray(optionsSelected)) dataSelectorToSend = []

    if(useSelectAllOption && isMulti) {
      //if allListWasSelected and there is change, it means it can't still be all selected 
      var allListWasSelected = false
      if(Array.isArray(dataToUse.values)) {
        allListWasSelected = dataToUse.values.some(isOption_All)
      }
  
      var allListIsSelected
      var optionsSelectedToUse
      var optionAllHasBeenUnselected
      if(allListWasSelected) {
        optionsSelectedToUse = [...optionsSelected].filter(isNotOption_All)
        var allOptionsAvailableWithoutAll = [...dataToUse.options].filter(isNotOption_All)
        optionAllHasBeenUnselected = allOptionsAvailableWithoutAll.length === optionsSelectedToUse.length
        allListIsSelected = false
      } else {
        optionsSelectedToUse = [...optionsSelected]
        optionAllHasBeenUnselected = false
        allListIsSelected = optionsSelectedToUse.some(isOption_All)
      }
  
      var newValues = []
      if(optionAllHasBeenUnselected) newValues = []
      else if(allListIsSelected) newValues = [...dataToUse.options]
      else if(optionsSelectedToUse.length === 1) newValues = optionsSelectedToUse[0]
      else if(optionsSelectedToUse.length > 1) newValues = optionsSelectedToUse
  
      dataSelectorToSend = optionsSelectedToUse
      if(Array.isArray(newValues)) {
        dataSelectorToSend = newValues.filter(isNotOption_All)
      }
  
      setDataToUse(previousState => ({...previousState, values: newValues }))
    } else {
      dataSelectorToSend = {...optionsSelected}
    }
    onChange(dataSelectorToSend)
  }

  return (
    <GenericFilterContainer data-testid="genericFilter" style={{height: "100%"}}>
      <Select
        {...dataToUse?.values && {value: dataToUse?.values}}
        placeholder={placeholder}
        aria-label={ariaLabel}
        styles={customStyles}
        hideSelectedOptions={false}
        closeMenuOnSelect={!isMulti}
        options={dataToUse.options}
        onChange={onChangeMiddleWare}
        defaultValue={defaultValue}
        components={components}
        customMultiOptionProps={{ checkmarkColor: inputBackgroundColor }}
        isClearable={isClearable}
        isSearchable={isSearchable}
        noOptionsMessage={() => noOptionsMessage}
        isMulti={isMulti}
        {...menuIsOpenProps}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
      />
    </GenericFilterContainer>
  )
}

GenericFilter.propTypes = {
  options: PropTypes.array,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  customMultiOption: PropTypes.func,
  customMultiValue: PropTypes.func,
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  isMulti: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  enableCustomMenuToggle: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  inputTextColor: PropTypes.string,
  inputBackgroundColor: PropTypes.string,
  inputHeight: PropTypes.string,
  useSelectAllOption: PropTypes.bool,
} 

GenericFilter.defaultProps = {
  options: [
    { value: 'chocolate', label: 'Chocolate' },
    { value: 'strawberry', label: 'Strawberry' },
    { value: 'vanilla', label: 'Vanilla' }
  ],
  placeholder: "Search...",
  noOptionsMessage: "No options",
  customMultiOption: CustomMultiOption,
  customMultiValue: CustomMultiValue,
  onChange: (optionsSelected) => console.log(optionsSelected),
  ariaLabel: "input",
  isMulti: true,
  menuIsOpen: true,
  enableCustomMenuToggle: false,
  isSearchable: true,
  isClearable: true,
  inputTextColor: "",
  inputBackgroundColor: "#fff",
  inputHeight: "50px",
  useSelectAllOption: false,
  borderRadius: "0px",
}

export default GenericFilter
